import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Injectable, inject, Injector, InjectionToken, signal, effect } from '@angular/core';
import {
  MatomoAction,
  VISIT_DIMENSION_IDS,
  ACTION_DIMENSION_IDS,
  MATOMO_APP_NAME,
  MATOMO_SESSION_KEYS,
  MATOMO_CONTEXT_CHANGED_EVENT,
} from './matomo-analytics.config';
import { makeRandomSessionId, fnv1a64Hex, computeMatomoId } from './matomo-identity.util';
import { UrlFlagsService, parseHostToken } from '../url-flags/url-flags.service';
import { ROUTE_DATA } from '../../constants/route';

type MatomoModuleName = 'Portal' | 'Task Manager';
type MatomoRouteSegments = string[] | null | undefined;

export type MatomoScriptCrossorigin = 'anonymous' | 'use-credentials' | '' | null;

export interface MatomoScriptDefaults {
  crossorigin: MatomoScriptCrossorigin;
}

export interface MatomoTrackerConfig {
  trackerUrl: string;
  siteId: string;
  environment: 'dev' | 'stage' | 'prod';
}

export const MATOMO_SCRIPT_DEFAULTS = new InjectionToken<MatomoScriptDefaults>(
  'MATOMO_SCRIPT_DEFAULTS',
  {
    factory: () => ({ crossorigin: 'anonymous' }),
  }
);

export const MATOMO_TRACKER_CONFIG = new InjectionToken<MatomoTrackerConfig>(
  'MATOMO_TRACKER_CONFIG',
  {
    factory: () => ({ trackerUrl: '', siteId: '', environment: 'dev' }),
  }
);

/**
 * Route analytics naming works in three modes:
 * 1. `data[ROUTE_DATA.matomoSegments] = ['Dashboard']` -> uses the explicit label.
 * 2. `data[ROUTE_DATA.matomoSegments]` is missing -> falls back to `route.path` (for example `teams`).
 * 3. `data[ROUTE_DATA.matomoSegments] = null` -> skips both the explicit label and the `route.path` fallback.
 */

@Injectable({
  providedIn: 'root',
})
export class MatomoTrackerService {
  private static _injector: Injector | null = null;
  public static get injector(): Injector | null {
    return MatomoTrackerService._injector;
  }

  /**
   * Compute matomo id for arbitrary inputs (host, sessionId, embedded) without storing.
   */
  public async computeMatomoIdFor(
    host?: string | null,
    sessionId?: string | null,
    embedded = false
  ): Promise<string> {
    const rawHost = host ?? (this.urlFlags.host ? this.urlFlags.host() : null) ?? '';
    const hostKey = parseHostToken(rawHost) || '';
    let sid = sessionId ?? sessionStorage.getItem(MATOMO_SESSION_KEYS.SESSION_ID) ?? null;
    if (!sid) {
      sid = makeRandomSessionId();
    }
    const embeddedSuffix = embedded ? '1' : '0';
    return computeMatomoId(hostKey, sid, embeddedSuffix);
  }

  public currentCategoryPath = signal<string>('');

  private _isInitialized = false;
  private _siteId = '';
  private _moduleName: MatomoModuleName = 'Portal';
  private readonly urlFlags = inject(UrlFlagsService);
  private readonly matomoScriptDefaults = inject(MATOMO_SCRIPT_DEFAULTS);
  private readonly matomoTrackerConfig = inject(MATOMO_TRACKER_CONFIG);

  private _currentMatomoId: string | null = null;

  constructor(injector: Injector = inject(Injector)) {
    MatomoTrackerService._injector ??= injector;
    // Watch UrlFlagsService host/embedded signals and regenerate ids when they change.
    try {
      // Keep previous values in closure-scoped variables to avoid mutating `this`.
      let prevHost: string | null | undefined = undefined;
      let prevEmbedded: boolean | undefined = undefined;
      // Use an effect to react to signal changes from UrlFlagsService.
      effect(() => {
        const host = this.urlFlags.host ? this.urlFlags.host() : null;
        const embedded = this.urlFlags.embedded ? this.urlFlags.embedded() : false;
        const priorHost = prevHost;
        const priorEmbedded = prevEmbedded;
        prevHost = host;
        prevEmbedded = embedded;
        if (priorHost !== undefined && priorEmbedded !== undefined) {
          if (priorHost !== host || priorEmbedded !== embedded) {
            if (this._isDebugLoggingEnabled()) {
              console.log('[Matomo Debug][SIGNAL_CONTEXT_CHANGED]', { host, embedded });
            }
            void this.regenerateSessionAndMatomoId().catch(() => void 0);
          }
        }
      });
    } catch {
      // effect may not be available in older runtimes; ignore if so
    }

    try {
      globalThis.addEventListener(MATOMO_CONTEXT_CHANGED_EVENT, () => {
        void this.regenerateSessionAndMatomoId().catch(() => void 0);
      });
    } catch {
      // ignore if global event listeners are unavailable
    }
  }

  private _getActiveRouteSnapshot(router: Router): ActivatedRouteSnapshot {
    let snapshot = router.routerState.snapshot.root;

    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }

    return snapshot;
  }

  private _getRouteDataSegments(routeSnapshot: ActivatedRouteSnapshot): string[] {
    const segments = routeSnapshot.data?.[ROUTE_DATA.matomoSegments] as MatomoRouteSegments;

    if (!Array.isArray(segments)) {
      return [];
    }

    return segments.map(segment => String(segment).trim()).filter(Boolean);
  }

  private _hasExplicitRouteDataSegments(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return ROUTE_DATA.matomoSegments in (routeSnapshot.data ?? {});
  }

  private _getStaticAnalyticsSegments(path?: string): string[] {
    if (!path) {
      return [];
    }

    return path
      .split('/')
      .map(segment => segment.trim())
      .filter(segment => segment.length > 0 && !segment.startsWith(':'));
  }

  private _getRouteAnalyticsPath(router: Router): string {
    const activeSnapshot = this._getActiveRouteSnapshot(router);
    const routeSegments = activeSnapshot.pathFromRoot.flatMap(routeSnapshot => {
      if (this._hasExplicitRouteDataSegments(routeSnapshot)) {
        return this._getRouteDataSegments(routeSnapshot);
      }

      return this._getStaticAnalyticsSegments(routeSnapshot.routeConfig?.path);
    });
    const analyticsSegments = routeSegments.filter(
      (segment, index) => index === 0 || routeSegments[index - 1] !== segment
    );

    return analyticsSegments.join('/');
  }

  private _normalizeEmbeddedPath(path: string): string {
    const normalizedSegments = path
      .split('?')[0]
      .split('/')
      .map(segment => segment.trim())
      .filter(Boolean);
    const normalizedPath = '/' + normalizedSegments.join('/');

    // Embedded-path normalization is deterministic; no debug logging here.

    return normalizedPath;
  }

  private _normalizePathByMode(
    path: string,
    action: MatomoAction | 'Visit',
    isEmbedded: boolean
  ): string {
    if (!isEmbedded) {
      return path;
    }

    return this._normalizeEmbeddedPath(path);
  }

  private _isDebugLoggingEnabled(): boolean {
    return this.matomoTrackerConfig.environment === 'dev';
  }

  private _getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem(MATOMO_SESSION_KEYS.SESSION_ID);
    if (!sessionId) {
      sessionId = makeRandomSessionId();
      try {
        sessionStorage.setItem(MATOMO_SESSION_KEYS.SESSION_ID, sessionId);
      } catch {
        /* ignore */
      }
    }

    return sessionId;
  }

  public ensureSessionId(): string {
    return this._getOrCreateSessionId();
  }

  private _getCurrentMatomoContext(sessionId = this._getOrCreateSessionId()): {
    hostKey: string;
    isEmbedded: boolean;
    sessionId: string;
  } {
    const rawHost = this.urlFlags.host ? this.urlFlags.host() : '';
    const hostKey = parseHostToken(rawHost) || '';
    const isEmbedded = this.urlFlags.embedded?.() ?? false;

    return { hostKey, isEmbedded, sessionId };
  }

  public getOrCreateMatomoId(): string {
    const { hostKey, isEmbedded, sessionId } = this._getCurrentMatomoContext();

    if (this._currentMatomoId) {
      return this._currentMatomoId;
    }

    const embeddedSuffix = isEmbedded ? '1' : '0';
    const input = `${hostKey}${sessionId}${embeddedSuffix}`;
    const id = fnv1a64Hex(input);

    this._currentMatomoId = id;
    if (this._isDebugLoggingEnabled()) {
      try {
        console.log('[Matomo Debug][GET_OR_CREATE_ID]', {
          id,
          host: hostKey,
          sessionId,
          isEmbedded,
        });
      } catch {
        /* ignore */
      }
    }
    return id;
  }

  /**
   * Recompute matomo_id (SHA-256 truncated) from the current URL context
   * (host + session_id + embedded). Always recomputes to guarantee freshness.
   */
  public async ensureMatomoId(): Promise<string> {
    const { hostKey, isEmbedded, sessionId } = this._getCurrentMatomoContext();

    const embeddedSuffix = isEmbedded ? '1' : '0';
    const id = await computeMatomoId(hostKey, sessionId, embeddedSuffix);

    this._currentMatomoId = id;
    if (this._isDebugLoggingEnabled()) {
      try {
        console.log('[Matomo Debug][ENSURE_ID]', {
          id,
          host: hostKey,
          sessionId,
          embedded: isEmbedded,
        });
      } catch {
        /* ignore */
      }
    }
    return id;
  }

  /**
   * Recompute matomo_id without changing session_id.
   * Keeps session_id stable so switching back to the same host produces the same matomo_id.
   */
  public async regenerateSessionAndMatomoId(): Promise<string> {
    // Clear the in-memory cache so getOrCreateMatomoId() won't return a stale value
    // before the async recomputation finishes.
    if (this._isDebugLoggingEnabled()) {
      try {
        console.log('[Matomo Debug][REGENERATE_START]', {
          href: globalThis.location.href,
          currentMatomoId: this._currentMatomoId,
          host: this.urlFlags.host?.() ?? null,
          embedded: this.urlFlags.embedded?.() ?? false,
          sessionId: sessionStorage.getItem(MATOMO_SESSION_KEYS.SESSION_ID),
        });
      } catch {
        /* ignore */
      }
    }
    this._currentMatomoId = null;

    const id = await this.ensureMatomoId();

    // If Matomo queue exists, update the user id immediately
    try {
      const windowWithPaq = globalThis as unknown as Window & { _paq?: unknown[] };
      const _paq = (windowWithPaq._paq = windowWithPaq._paq || []);
      _paq.push(['setUserId', id]);
    } catch {
      // ignore
    }
    if (this._isDebugLoggingEnabled()) {
      try {
        console.log('[Matomo Debug][REGENERATE_DONE]', {
          href: globalThis.location.href,
          newMatomoId: id,
          host: this.urlFlags.host?.() ?? null,
          embedded: this.urlFlags.embedded?.() ?? false,
          sessionId: sessionStorage.getItem(MATOMO_SESSION_KEYS.SESSION_ID),
        });
      } catch {
        /* ignore */
      }
    }

    return id;
  }

  /**
   * Generate a deterministic matomo id for given inputs (for debugging/testing).
   * Returns the same format as the live generator: optional `hostKey_` prefix + hex id.
   */
  public generateMatomoIdFor(
    host: string | null | undefined,
    email: string | null | undefined,
    embedded = false
  ): string {
    const hostKey = parseHostToken(host) || '';
    const userValue = String(email ?? '').trim();
    const embeddedSuffix = embedded ? '1' : '0';
    const input = `${hostKey}${userValue}${embeddedSuffix}`;
    return fnv1a64Hex(input);
  }

  private _buildVisitDimensionCommands(): unknown[] {
    const isEmbedded = this.urlFlags.embedded?.() ?? false;
    const originValue = globalThis.location.origin ?? 'unknown';
    const visitAppId = VISIT_DIMENSION_IDS['application'];
    const visitModeId = VISIT_DIMENSION_IDS['mode'];
    const visitHostId = VISIT_DIMENSION_IDS['host'];
    const visitWebsiteId = VISIT_DIMENSION_IDS['website'];

    const modeValue = isEmbedded ? 'embedded' : 'non-embedded';
    const rawHost = this.urlFlags.host ? this.urlFlags.host() : null;
    const hostValue = parseHostToken(rawHost) || '';

    const commands = [
      ...(visitAppId ? [['setCustomDimension', visitAppId, MATOMO_APP_NAME]] : []),
      ...(visitModeId ? [['setCustomDimension', visitModeId, modeValue]] : []),
      ...(visitHostId ? [['setCustomDimension', visitHostId, hostValue]] : []),
      ...(visitWebsiteId ? [['setCustomDimension', visitWebsiteId, originValue]] : []),
    ];

    // Visit-dimension debug logging removed to avoid noisy output; setup snapshot is sufficient.

    return commands;
  }

  private _buildActionDimensionCommands(): unknown[] {
    const isEmbedded = this.urlFlags.embedded?.() ?? false;
    const originValue = globalThis.location.origin ?? 'unknown';
    const actionAppId = ACTION_DIMENSION_IDS['application'];
    const actionModeId = ACTION_DIMENSION_IDS['mode'];
    const actionHostId = ACTION_DIMENSION_IDS['host'];
    const actionWebsiteId = ACTION_DIMENSION_IDS['website'];

    const modeValue = isEmbedded ? 'embedded' : 'non-embedded';
    const rawHost = this.urlFlags.host ? this.urlFlags.host() : null;
    const hostValue = parseHostToken(rawHost) || '';

    const commands = [
      ...(actionAppId ? [['setCustomDimension', actionAppId, MATOMO_APP_NAME]] : []),
      ...(actionModeId ? [['setCustomDimension', actionModeId, modeValue]] : []),
      ...(actionHostId ? [['setCustomDimension', actionHostId, hostValue]] : []),
      ...(actionWebsiteId ? [['setCustomDimension', actionWebsiteId, originValue]] : []),
    ];

    // Action-dimension debug logging removed to avoid noisy output; setup snapshot is sufficient.

    return commands;
  }

  private async _reportRouteVisit(router: Router): Promise<void> {
    await this.ensureMatomoId();
    const normalizedPath = this._getRouteAnalyticsPath(router);
    this.currentCategoryPath.set(normalizedPath);

    this.reportAction(normalizedPath, 'Visit');
  }

  private _trackVisit(router: Router): void {
    if (!router?.events) return;

    void this._reportRouteVisit(router);

    router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      void this._reportRouteVisit(router);
    });
  }

  public initRouterTracking(router: Router): void {
    this._trackVisit(router);
  }

  public setupAnalytics(module: MatomoModuleName): void {
    if (this._isInitialized) return;

    this._moduleName = module;
    this._siteId = this.matomoTrackerConfig.siteId;

    const windowWithPaq = globalThis as unknown as Window & { _paq?: unknown[] };
    const _paq = (windowWithPaq._paq = windowWithPaq._paq || []);
    const trackerUrl = this.matomoTrackerConfig.trackerUrl;

    if (!trackerUrl || !this._siteId) {
      console.warn('Matomo: Tracker configuration is missing trackerUrl or siteId.');
      return;
    }

    const setupCommands: unknown[] = [
      ['setDownloadExtensions', 'ifc|skp|bcf|bcfzip|log|xlsx|glb'],
      ['enableLinkTracking'],
      ['setTrackerUrl', trackerUrl + 'matomo.php'],
      ['setSiteId', this._siteId],
      ['disableCookies'],
    ];

    _paq.push(...setupCommands);

    if (this._isDebugLoggingEnabled()) {
      try {
        console.log('[Matomo OCP][PAQ SETUP SNAPSHOT]', {
          setupCommands,
          paqSnapshot: _paq.slice?.() ?? _paq,
        });
      } catch {
        // ignore logging errors
      }
    }

    const d = globalThis.document;
    const g = d.createElement('script');
    const s = d.getElementsByTagName('script')[0];
    g.type = 'text/javascript';
    g.async = true;
    g.src = trackerUrl + 'matomo.js';

    if (this.matomoScriptDefaults.crossorigin !== null) {
      g.crossOrigin = this.matomoScriptDefaults.crossorigin;
    }

    if (s?.parentNode) {
      s.parentNode.insertBefore(g, s);
    } else {
      d.head.appendChild(g);
    }

    // Debug helper removed to avoid global namespace pollution in production.

    this._isInitialized = true;
  }

  public reportAction(path: string, action: MatomoAction | 'Visit'): void {
    if (!this._isInitialized) {
      console.warn('Matomo: Service was not initialized via setupAnalytics().');
      return;
    }

    const isEmbedded = this.urlFlags.embedded?.() ?? false;
    let normalizedPath = this._normalizePathByMode(path, action, isEmbedded);
    normalizedPath = normalizedPath.replace(/\/{2,}/g, '/');

    const windowWithPaq = globalThis as unknown as Window & { _paq?: unknown[] };
    const _paq = (windowWithPaq._paq = windowWithPaq._paq || []);
    const embedStatus = isEmbedded ? 'Embedded' : 'Non-Embedded';
    const pathSegments = normalizedPath.split('/').filter(Boolean);
    const titleCleanPath = pathSegments.join('/');
    const titlePath = [MATOMO_APP_NAME, embedStatus, this._moduleName, titleCleanPath, action]
      .filter(Boolean)
      .join('/');
    const urlCleanPath = pathSegments.join('/');
    const customVirtualUrl = `${globalThis.location.origin}/${[
      MATOMO_APP_NAME,
      embedStatus,
      this._moduleName,
      urlCleanPath,
      action,
    ]
      .filter(Boolean)
      .join('/')}`;

    const actionDimensionCommands = this._buildActionDimensionCommands();

    // Intentionally silent in runtime; only setup logs are emitted in dev/localhost.

    const isVisit = action === 'Visit';
    const visitDimensionCommands = isVisit ? this._buildVisitDimensionCommands() : [];
    // Action dimensions only on non-Visit actions (Click, Select, etc.)
    const actionDims = isVisit ? [] : actionDimensionCommands;

    const matomoId = this.getOrCreateMatomoId();
    const idCmd = matomoId ? [['setUserId', matomoId]] : [];

    const commands: unknown[] = [
      // Ensure a stable matomo user id is set before sending any pageview/event.
      ...idCmd,
      // Visit-scoped dimensions only on Visit
      ...visitDimensionCommands,
      // Action-scoped dimensions only on Click/Select/etc.
      ...actionDims,
      ['setCustomUrl', customVirtualUrl],
      ['setDocumentTitle', titlePath],
      ['trackPageView'],
    ];

    if (this._isDebugLoggingEnabled()) {
      try {
        console.log('[Matomo OCP][PAQ SEND]', {
          action,
          titlePath,
          customVirtualUrl,
          commands,
        });
      } catch {
        // ignore logging errors
      }
    }

    // Push all commands in the correct order so Matomo associates the custom dimension with the visit/action
    _paq.push(...commands);
  }
}
